import React, { useState } from "react";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/lib/LanguageContext";
import { cn } from "../../../lib/utils";
import { crmErrorMessage } from "../api/crmErrors";
import { FOCUS_RING } from "../ui/classes";
import CrmTooltip from "../ui/CrmTooltip";
import { CRM } from "../../../constants/testIds/crm";

/**
 * The Confirmed badge of a Today card (spec): a green tick when the client confirmed
 * from the reminder email, a grey "?" when not — and tapping the "?" resends the
 * reminder at once. While the practice has not turned reminders on
 * (`GET /api/crm/setup` → `cards.reminders.done == false`) there is nothing to resend:
 * the "?" is disabled and says why.
 */
export default function ConfirmBadge({ bookingId, confirmed, disabled = false, remindersOn = true, onResend }) {
  const t = useT("crm");
  const tc = useT("common");
  const [busy, setBusy] = useState(false);
  if (confirmed) {
    return (
      <span data-testid={CRM.todayConfirm(bookingId)} data-confirmed="true" role="img" aria-label={t("today.confirmed")}
        className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-eh-sage-deep text-eh-bg">
        <Check size={16} aria-hidden="true" />
      </span>
    );
  }
  const resend = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      await onResend(bookingId);
      toast.success(t("today.reminderSent"));
    } catch (err) {
      toast.error(crmErrorMessage(err, t, tc));
    } finally {
      setBusy(false);
    }
  };
  const button = (
    <button type="button" onClick={resend} onKeyDown={(e) => e.stopPropagation()} disabled={busy || disabled || !remindersOn} data-testid={CRM.todayConfirm(bookingId)} data-confirmed="false"
      aria-label={t("today.resendAria")} title={t("today.resendAria")}
      className={cn("inline-flex h-7 w-7 items-center justify-center rounded-full bg-eh-cream-deep text-sm font-bold text-eh-ink disabled:opacity-50", FOCUS_RING)}>
      ?
    </button>
  );
  return remindersOn ? button : <CrmTooltip content={t("today.remindersOff")} testId={CRM.todayRemindersOffTooltip}>{button}</CrmTooltip>;
}
